import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Clock, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);
  const [isOutsideHours, setIsOutsideHours] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check localStorage for privacy acceptance
  useEffect(() => {
    const accepted = localStorage.getItem('jannesmann_chat_privacy_accepted');
    if (accepted === 'true') {
      setHasAcceptedPrivacy(true);
    }
    const storedSessionId = localStorage.getItem('jannesmann_chat_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && hasAcceptedPrivacy && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, hasAcceptedPrivacy]);

  // Hide bubble after opening once
  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  const handleAcceptPrivacy = () => {
    setHasAcceptedPrivacy(true);
    localStorage.setItem('jannesmann_chat_privacy_accepted', 'true');
    
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: 'Herzlich willkommen bei Jannesmann Baumaschinen! 👋\n\nIch bin Ihr KI-Berater und helfe Ihnen gerne bei Fragen zu:\n\n• Baggern, Radladern, Gabelstaplern\n• Preisen und Verfügbarkeit\n• Lieferung und Abholung\n• Zahlungsarten und Finanzierung\n• Garantie und Service\n\nWie kann ich Ihnen heute helfen?',
      timestamp: new Date().toISOString()
    }]);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          history: messages.slice(-10)
        }),
      });

      const data = await response.json();

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('jannesmann_chat_session_id', data.session_id);
      }

      setIsOutsideHours(data.is_outside_business_hours);

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter 02041 9877499.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Privacy consent screen
  const PrivacyConsent = () => (
    <div className="p-4 space-y-4">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Bot className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="font-semibold text-gray-900">Jannesmann KI-Berater</h3>
        <p className="text-sm text-gray-500">Ihr digitaler Assistent</p>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 space-y-2">
        <p className="font-medium text-gray-700">Datenschutz-Hinweis:</p>
        <ul className="space-y-1 text-xs">
          <li>• Der Chatverlauf wird anonymisiert gespeichert</li>
          <li>• Keine personenbezogenen Daten werden erfasst</li>
          <li>• Nutzung zur Verbesserung unseres Services</li>
          <li>• DSGVO-konform</li>
        </ul>
      </div>

      <Button
        onClick={handleAcceptPrivacy}
        className="w-full bg-red-600 hover:bg-red-700 text-white"
        data-testid="chat-accept-privacy"
      >
        Verstanden, Chat starten
      </Button>
      
      <p className="text-xs text-center text-gray-400">
        Mit dem Start akzeptieren Sie unsere{' '}
        <a href="/datenschutz" className="text-red-600 hover:underline" target="_blank">
          Datenschutzerklärung
        </a>
      </p>
    </div>
  );

  return (
    <>
      {/* Chat Button - nur roter Punkt/Icon, kein Hinweistext */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 md:right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-800 rotate-0' 
            : 'bg-red-600 hover:bg-red-700 hover:scale-105'
        }`}
        data-testid="chat-toggle-button"
        aria-label={isOpen ? 'Chat schließen' : 'Chat öffnen'}
      >
        {isOpen ? (
          <ChevronDown className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed inset-0 md:inset-auto md:bottom-20 md:right-6 z-50 w-full md:w-[380px] h-full md:h-[550px] bg-white md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-0 md:border md:border-gray-200 animate-slide-up"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Jannesmann KI-Berater</h3>
                <div className="flex items-center gap-1 text-xs text-white/80">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  <span>Online</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Chat minimieren"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {!hasAcceptedPrivacy ? (
            <PrivacyConsent />
          ) : (
            <>
              {/* Outside Hours Notice */}
              {isOutsideHours && messages.length <= 2 && (
                <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-xs text-blue-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>Außerhalb der Geschäftszeiten – persönliche Beratung am nächsten Werktag.</span>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-end gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      {/* Avatar */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' 
                          ? 'bg-gray-200' 
                          : 'bg-red-100'
                      }`}>
                        {message.role === 'user' 
                          ? <User className="w-4 h-4 text-gray-600" />
                          : <Bot className="w-4 h-4 text-red-600" />
                        }
                      </div>
                      
                      {/* Message Bubble */}
                      <div className={`rounded-2xl px-4 py-2.5 ${
                        message.role === 'user'
                          ? 'bg-red-600 text-white rounded-br-md'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className={`text-[10px] mt-1 ${
                          message.role === 'user' ? 'text-red-200' : 'text-gray-400'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-end gap-2">
                      <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 bg-white border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ihre Frage..."
                    className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-all"
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="w-10 h-10 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                    data-testid="chat-send-button"
                    aria-label="Nachricht senden"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Send className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>
                
                {/* Quick Contact */}
                <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
                  <a href="tel:+4920419877499" className="flex items-center gap-1 hover:text-red-600 transition-colors">
                    <Phone className="w-3 h-3" />
                    <span>Anrufen</span>
                  </a>
                  <span className="text-gray-300">|</span>
                  <a href="mailto:info@jannesmann.de" className="flex items-center gap-1 hover:text-red-600 transition-colors">
                    <Mail className="w-3 h-3" />
                    <span>E-Mail</span>
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Animations CSS */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
